import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import Teachr from "../../Objects/models/Teachr";
import { getActiveCourse, getPendingCourse } from "../../Redux/Actions/PunctualCourseActions";
import { CourseEditLocationState, EditableCourse } from "./types";
import { editableFromCourse } from "./courseEditRules";

/**
 * Cours à modifier : d'abord celui passé par la liste (location.state), sinon
 * recherche dans les listes déjà chargées (cours à venir, cours du mois),
 * sinon un chargement des premières pages de ces listes. Un rechargement de la
 * page retrouve ainsi le cours sans nouvel appel dédié.
 */
const useEditableCourse = (teachr: Teachr | null | undefined, courseId: number | null) => {
    const location = useLocation();
    const dispatch = useDispatch<any>();
    const state = (location.state as CourseEditLocationState | null) ?? null;
    const pending = useSelector((s: any) => s.punctualCourse?.pendingCourse);
    const active = useSelector((s: any) => s.punctualCourse?.activeCourse);
    const [fetched, setFetched] = useState(false);
    const [loading, setLoading] = useState(false);

    const fromState = useMemo<EditableCourse | null>(() => {
        if (!state?.course || !courseId || Number(state.course?.id) !== courseId) return null;
        return editableFromCourse(state.course, state, teachr);
    }, [state, courseId, teachr]);

    const fromLists = useMemo<EditableCourse | null>(() => {
        if (!courseId) return null;
        // cours à venir : liste plate, chaque ligne porte clientId / forChild_id / startAt
        const flat: any[] = Array.isArray(pending?.courses) ? pending.courses : [];
        const row = flat.find((c) => Number(c?.id) === courseId);
        if (row) return editableFromCourse(row, { clientId: row.clientId, forChildId: row.forChild_id, startAt: row.startAt }, teachr);
        // cours du mois : groupés par élève
        const groups: any[] = Array.isArray(active?.courses) ? active.courses : [];
        for (const group of groups) {
            const found = (Array.isArray(group?.courses) ? group.courses : []).find((c: any) => Number(c?.id) === courseId);
            if (found) {
                const enriched = { ...found, forChild_id: group.forChild_id, clientId: group.clientId, startAt: group.startAt, firstName: group.firstName, lastName: group.lastName };
                return editableFromCourse(enriched, { clientId: group.clientId, forChildId: group.forChild_id, startAt: group.startAt }, teachr);
            }
        }
        return null;
    }, [courseId, pending, active, teachr]);

    const course = fromState ?? fromLists;

    useEffect(() => {
        if (course || fetched || !teachr?.id || !courseId) return;
        setFetched(true);
        setLoading(true);
        const global = { teachr, nameSearch: "", subjectSearch: "", startDateSearch: "", endDateSearch: "" };
        Promise.all([
            dispatch(getPendingCourse({ currentPagePending: 0, limitPending: 100, isData: false, global })),
            dispatch(getActiveCourse({ currentPage: 0, limit: 100, isData: false, global })),
        ]).finally(() => setLoading(false));
    }, [course, fetched, teachr, courseId, dispatch]);

    return { course, loading: loading || (!course && !fetched) };
};

export default useEditableCourse;
